import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageShell from "@/components/PageShell";
import { CITIES } from "@/lib/pages-data";

const SITE = "https://thecoventures.com";

export default function CityIndexPage() {
  const list = Object.values(CITIES);
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: list.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: `Vending machines in ${c.name}`,
      url: `${SITE}/vending-machines/${c.slug}`,
    })),
  };

  return (
    <PageShell>
      <Seo
        title="Vending Machines Across India — City Coverage | Thecoventures"
        description="Buy vending machines across India — Vadodara, Ahmedabad, Mumbai, Pune, Bangalore, Delhi NCR, Hyderabad. Local install, on-site AMC and 98% uptime SLA."
        canonical={`${SITE}/vending-machines`}
        keywords="vending machine India cities, vending machine dealer, vending machine supplier"
        jsonLd={itemListSchema}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
          Pan-India install & service
        </div>
        <h1
          data-testid="cities-h1"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950 max-w-3xl"
        >
          Vending machines across India.
        </h1>
        <p className="mt-5 text-zinc-600 max-w-3xl md:text-lg">
          Thecoventures installs and services smart, cashless vending machines
          across India &mdash; from IT parks and startup campuses in Bangalore,
          Hyderabad and Pune, to manufacturing clusters around Vadodara,
          Ahmedabad and Delhi NCR, to hospital, retail and factory floors in
          Mumbai. Every deployment ships UPI-ready, streams live telemetry to a
          cloud dashboard and is backed by our pan-India AMC network with a 98%
          uptime SLA.
        </p>
        <p className="mt-4 text-zinc-600 max-w-3xl">
          Pick the city closest to your site to see local coverage details,
          suggested machine models and typical corporate parks, factory belts
          and hospital campuses we already serve. If your city isn&apos;t listed
          below, we still ship &mdash; write to us and we&apos;ll confirm the
          nearest technician hub and expected install timeline. Delivery is
          typically 7&ndash;14 business days depending on ready stock, and
          payment-gateway onboarding takes an additional 15&ndash;20 days
          subject to the gateway&apos;s document policy. Prices start from
          &#8377;1.5&nbsp;lakhs (transport extra) &mdash; and we send a written
          quote within 24 hours.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {list.map((c) => (
            <Link
              key={c.slug}
              to={`/vending-machines/${c.slug}`}
              data-testid={`city-card-${c.slug}`}
              className="group rounded-3xl border border-zinc-200 bg-white p-6 md:p-7 hover:border-emerald-400 hover:-translate-y-1 hover:shadow-xl hover:shadow-zinc-900/5 transition"
            >
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 mb-4">
                <MapPin className="w-3 h-3" /> {c.state}
              </div>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
                Vending machines in {c.name}
              </h2>
              <p className="mt-2 text-sm text-zinc-600 line-clamp-2">{c.highlights[0]}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-zinc-900 group-hover:text-emerald-700 transition">
                Explore {c.name} <ArrowUpRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 rounded-3xl border border-zinc-200 bg-zinc-50 p-6 md:p-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 mb-3">
            What&apos;s included in every city
          </div>
          <p className="text-zinc-700 max-w-4xl">
            Whichever city you&apos;re in, every Thecoventures vending machine
            arrives with UPI / QR / card / wallet payments configured, cloud
            telemetry for stock and sales, a customised planogram built around
            your team&apos;s tastes, on-site technician training for your admin
            or facilities lead, and an optional Annual Maintenance Contract with
            a 98% uptime SLA. RFID / prepaid card / employee-card dispensing is
            available as a chargeable add-on for offices, factories and
            hospitals that need per-employee limits or audit logs. For factory
            and hospital sites we also offer the Refuel SAFE PPE dispenser
            &mdash; useful for ISO / EHS audits &mdash; with authenticated
            issuance of gloves, masks, glasses and consumables.
          </p>
        </div>
      </section>
    </PageShell>
  );
}
